/* eslint-disable max-lines */
import { message as $message } from 'antd'
import { getToken, setToken, clearToken } from '@/utils/token'
import { setAuthority } from '@/utils/authority'
import { getTerminaDomain } from '@/utils/utils'
import { refreshToken, updateLastLoginTimeAndSide, canChangeUserIdentify, getContractVersion } from '@/services/login'
import {
    getUserInfo,
    getDirectInfo,
    queryUserConfig,
    partnerCheckApi,
    addUserConfig,
    getPlatToken,
    offlineFreeVersionUsingGET,
    acquireWmsInfo
} from '@/services/user'
import { isQualityApi } from '@/services/supplier/supplierUser'
import { getSimpleQueryUserModular } from '@/services/subAccount'
import { captureException } from '@/utils/track'
import { ERROR_TEXT, BLANK_TEXT } from '@/modules/user/constants'
import * as Sentry from '@sentry/react'
import { cacheRequestWrap } from '@/utils/requestUtils'
import { createErrorText } from '@/utils/request'
import { queryCoIdIsBigShop } from '@/services/distribution'
import { delay } from '@/utils/utils'

const errorUser = {
    coId: ERROR_TEXT,
    coName: ERROR_TEXT, // ERP名称
    supplierId: ERROR_TEXT,
    userName: BLANK_TEXT,
    headPic: ERROR_TEXT,
    supplierName: BLANK_TEXT,
    supplierLogo: ERROR_TEXT,
    loginName: ERROR_TEXT,
    phone: ERROR_TEXT,
    uid: ERROR_TEXT,
    isPrimaryAccount: false, // 与isAdmin等价(废弃)
    isAdmin: false, // 管理员
    isAgent: false, // 是否代理登录
    currentUserName: ERROR_TEXT, // 代理登录的中台用户名
    isSteward: false, // 维护员
    contractVersion: ERROR_TEXT,
    appRole: '2',
    appVersion: '1', // 默认为免费版
    hasCoChannel: true, // 是否已经初始化分销商身份
    tagNames: ERROR_TEXT,
    checkInTime: null,
    userCreated: null,
    // channel
    channelId: ERROR_TEXT,
    erpLabelsJson: null,
    labelsJson: [],
    userRole: ERROR_TEXT,
    channelAuditStatus: 'P',
    channelName: BLANK_TEXT,
    hasCoSupplier: true,
    wmsInfo: {
        branchPartner: false,
        mainWarehouseCoId: ERROR_TEXT
    } // 分仓角色（打单模块）
}

const UserModel = {
    namespace: 'user',
    state: {
        currentUser: null, // 当前用户信息
        config: {}, // 用户配置信息，如列配置
        isBigShop: undefined, // 当前用户是否大店
        isFenCang: false, // 当前用户是否分仓用户
        permissionPoint: null, // erp自定义权限信息
        erpToken: null, // 存储 ERP 令牌
        erpTokenFetchTime: null, // 存储获取令牌的时间戳
        fetchingErpToken: false, // 标识是否正在获取令牌
        isFreeContractVersion: false, // 是否免费版合同版本
        contractVersion: null // 用户合同版本
    },
    effects: {
        // 获取 ERP 令牌的 effect
        *fetchErpToken(_, { call, put }) {
            yield put({ type: 'setFetchingErpToken', payload: true }) // 设置正在获取令牌的标识

            try {
                const response = yield call(getPlatToken) // 调用获取令牌的服务
                const { success, data, message } = response

                if (success) {
                    yield put({
                        type: 'saveErpToken',
                        payload: {
                            token: data,
                            fetchTime: new Date().getTime() // 保存获取令牌的时间戳
                        }
                    })
                    yield put({ type: 'setFetchingErpToken', payload: false }) // 重置正在获取令牌的标识
                    return data
                } else {
                    $message.error(message) // 显示错误信息
                    yield put({ type: 'setFetchingErpToken', payload: false })
                    return Promise.reject(new Error(message))
                }
            } catch (e) {
                console.error('获取 ERP 令牌失败', e)
                yield put({ type: 'setFetchingErpToken', payload: false })
                return Promise.reject(new Error('获取 ERP 令牌失败'))
            }
        },

        // 检查并获取 ERP 令牌的 effect
        *getErpToken(_, { put, select }) {
            const { erpToken, erpTokenFetchTime, fetchingErpToken } = yield select(state => state.user)
            const tokenValidDuration = 30 * 60 * 1000 // 令牌有效期为 30 分钟
            const now = new Date().getTime()
            let nowFetchingErpToken = fetchingErpToken

            // 判断令牌是否存在或已过期
            if (!erpToken || !erpTokenFetchTime || now - erpTokenFetchTime > tokenValidDuration) {
                if (!nowFetchingErpToken) {
                    yield put({ type: 'fetchErpToken' }) // 获取新的令牌
                    nowFetchingErpToken = true
                }
                // 等待当前的获取请求完成
                // eslint-disable-next-line no-unmodified-loop-condition
                while (nowFetchingErpToken) {
                    yield delay(100)
                    const { fetchingErpToken: newFetchingErpToken } = yield select(state => state.user)
                    if (!newFetchingErpToken) break
                }
            }

            const newToken = yield select(state => state.user.erpToken) // 获取新的令牌
            return newToken
        },
        *fetchCurrent(_, { call, put, all }) {
            const { inviteId, enableWms } = _
            let data = {}
            try {
                const [{ success, data: resData, message } = {}, wmsResult = {}] = yield all(
                    [
                        call(cacheRequestWrap(getUserInfo, 'getUserInfo'), {
                            terminalName: PROJECT_CONFIG.terminalName,
                            inviteId
                        }),
                        enableWms ? call(acquireWmsInfo) : false
                    ].filter(Boolean)
                )

                if (success) {
                    // 排除掉，有问题要在开发阶段发现，避免带到线上
                    delete resData.wmsInfo
                    data = resData
                } else {
                    $message.error(message)
                    return Promise.reject(new Error('REJECT_获取用户信息失败'))
                }
                if (enableWms) {
                    if (wmsResult.success) {
                        data.wmsInfo = wmsResult.data
                    } else {
                        $message.error(wmsResult.message)
                        return Promise.reject(new Error('REJECT_获取主分仓信息失败'))
                    }
                }
            } catch (e) {
                console.error('获取用户信息失败', e)
                if (e?.message === createErrorText) {
                    return Promise.reject(new Error('REJECT_获取用户信息失败'))
                }
                Sentry.setExtra('error', { error: JSON.stringify(e, null, 2) })

                Sentry.captureException(new Error('获取用户信息失败 catch'))

                data = errorUser
            }

            const authority = data && Array.isArray(data.routers) ? data.routers.map(v => v.path) : []

            setAuthority(authority)

            yield put({
                type: 'saveCurrentUser',
                payload: data || {}
            })

            if (data.coId === 'error') {
                return Promise.reject(new Error('获取用户信息失败'))
            }
            try {
                yield put({ type: 'fetchIsFreeContractVersion' })
            } catch (error) {
                console.error(error)
            }

            return data
        },
        /** 是否免费版合同版本 */
        *fetchIsFreeContractVersion(_, { call, put }) {
            const { success, data, message } = yield call(offlineFreeVersionUsingGET)

            if (success) {
                yield put({
                    type: 'update',
                    payload: { isFreeContractVersion: data }
                })
                return data
            } else {
                $message.error(message)
                return null
            }
        },
        *fetchSelfIsBigShop(_, { call, select, put }) {
            try {
                const coId = yield select(state => state.user.currentUser?.coId)

                const { success, data, message } = yield call(queryCoIdIsBigShop, { coId })

                if (success) {
                    const isBigShop = !!data

                    yield put({
                        type: 'update',
                        payload: { isBigShop }
                    })
                    return isBigShop
                } else {
                    $message.error(message)
                    return null
                }
            } catch (e) {
                console.error('获取自己是否大店失败', e)
            }

            return Promise.reject(new Error('获取自己是否大店失败'))
        },
        *fetchDirector(_, { call }) {
            try {
                const { success, data, message } = yield call(getDirectInfo)

                if (success) {
                    return data
                } else {
                    $message.error(message)
                    return null
                }
            } catch (e) {
                console.error('获取用户权限点失败', e)
            }

            return Promise.reject(new Error('获取用户权限点失败'))
        },
        *fetchIsQuality(_, { call, put }) {
            let isTopQualitySupplier = false
            try {
                const { success, data, message } = yield call(isQualityApi)
                if (success) {
                    isTopQualitySupplier = data
                } else {
                    $message.error(message)
                }
                yield put({
                    type: 'saveCurrentUser',
                    payload: { isTopQualitySupplier }
                })
            } catch (e) {
                console.error('获取是否优质分销商失败', e)
                yield put({
                    type: 'saveCurrentUser',
                    payload: { isTopQualitySupplier }
                })
            }
        },
        *fetchUserPermissionPoints({ uid }, { call, put, select }) {
            try {
                const mUid = yield select(state => state.user.currentUser?.uid)

                const { success, data, message } = yield call(getSimpleQueryUserModular, {
                    uid: uid || mUid || ''
                })

                if (success) {
                    yield put({ type: 'saveUserPermissionPoint', payload: data })
                    return
                } else {
                    $message.error(message)
                    captureException(message, { anchor: 'getPermissionError' })
                    yield put({ type: 'fetchUserPermissionPoints2', payload: { uid } })
                    return null
                }
            } catch (e) {
                console.error('获取功能点权限失败', e)
                yield put({ type: 'fetchUserPermissionPoints2', payload: { uid } })
            }
        },
        // 临时处理
        *fetchUserPermissionPoints2({ uid }, { call, put, select }) {
            try {
                const mUid = yield select(state => state.user.currentUser?.uid)

                const { success, data, message } = yield call(getSimpleQueryUserModular, {
                    uid: uid || mUid || ''
                })

                if (success) {
                    yield put({ type: 'saveUserPermissionPoint', payload: data })
                    return
                } else {
                    $message.error(message)
                    return null
                }
            } catch (e) {
                console.error('再次获取功能点权限失败', e)
            }
        },

        *refreshToken(_, { call, put }) {
            const token = getToken() // 这个token 不能直接刷新，暂时不使用

            try {
                const { success, message, data } = yield call(refreshToken, token)

                if (success) {
                    yield put({
                        type: 'saveToken',
                        payload: data
                    })

                    return
                } else {
                    $message.error(message)
                }
            } catch (e) {
                console.error('refreshToken error', e)
            }
            yield put({
                type: 'login/clearLoginInfo'
            })
            Sentry.captureException(new Error('刷新token失败'))
        },
        *refreshRole({ payload }, { call }) {
            try {
                const { success, message } = yield call(updateLastLoginTimeAndSide, payload)

                if (success) {
                    // yield put({
                    //     type: 'saveRole',
                    //     payload: payload
                    // })
                } else {
                    $message.error(message)
                    throw new Error(message)
                }
            } catch (e) {
                console.error('refreshRole error', e)
                return Promise.reject(e)
            }
        },
        *roleChoose({ cb }, { call }) {
            try {
                const { success, message } = yield call(canChangeUserIdentify)

                if (success) {
                    if (cb) {
                        cb()
                    } else {
                        const accountPath = `${getTerminaDomain(PROJECT_CONFIG.accountTerminalName)}/role/choose`
                        window.location.href = accountPath
                    }
                } else {
                    $message.error(message)
                }
            } catch (e) {
                console.error('roleChoose error', e)
                return Promise.reject(e)
            }
        },
        *fetchConfig(_, { call, put }) {
            const { success, message, data } = yield call(queryUserConfig)

            if (success) {
                yield put({
                    type: 'saveConfig',
                    payload: data
                })
            } else {
                $message.error(message)
            }
        },
        *getOwnerWareHouse(_, { call, put }) {
            try {
                const { success, data, message } = yield call(partnerCheckApi)

                if (success) {
                    const isFenCang = data?.branchPartner
                    yield put({
                        type: 'update',
                        payload: { isFenCang }
                    })
                    return isFenCang
                } else {
                    $message.error(message)
                    return null
                }
            } catch (e) {
                console.error('获取是否分仓失败', e)
            }

            return Promise.reject(new Error('获取自己是否分仓失败'))
        },
        *addConfig({ payload }, { call, put }) {
            const { configKey, configValue } = payload

            yield put({
                type: 'updateConfig',
                payload: {
                    [configKey]: configValue
                }
            })

            const { success, message } = yield call(addUserConfig, payload)

            if (!success) {
                $message.error(message)
                return Promise.reject(new Error(message || '保存失败'))
            }

            return {
                success,
                message
            }
        },
        *fetchGetContractVersion(_, { call, put }) {
            try {
                const { success, data, message } = yield call(getContractVersion)

                if (success) {
                    yield put({
                        type: 'getContractVersionFn',
                        payload: data || []
                    })

                    return data
                } else {
                    $message.error(message)
                }
            } catch (e) {
                console.error('获取用户合同版本失败', e)
            }

            return Promise.reject(new Error('获取用户合同版本失败'))
        }
    },
    reducers: {
        getContractVersionFn(state, { payload }) {
            return { ...state, contractVersion: payload }
        },
        // 保存 ERP 令牌和获取时间的 reducer
        saveErpToken(state, { payload }) {
            return {
                ...state,
                erpToken: payload.token,
                erpTokenFetchTime: payload.fetchTime
            }
        },
        // 设置正在获取令牌标识的 reducer
        setFetchingErpToken(state, { payload }) {
            return { ...state, fetchingErpToken: payload }
        },
        saveCurrentUser(state, { payload }) {
            return { ...state, currentUser: { ...state.currentUser, ...payload } }
        },
        clearCurrentUser(state) {
            return {
                ...state,
                currentUser: null
            }
        },
        saveToken(state, { payload }) {
            setToken(payload)

            return {
                ...state,
                token: payload
            }
        },
        saveUserPermissionPoint(state, { payload }) {
            return {
                ...state,
                permissionPoint: payload
            }
        },

        clearToken(state) {
            clearToken()

            return {
                ...state,
                token: null
            }
        },
        changeNotifyCount(
            state = {
                currentUser: {}
            },
            action
        ) {
            return {
                ...state,
                currentUser: {
                    ...state.currentUser,
                    notifyCount: action.payload.totalCount,
                    unreadCount: action.payload.unreadCount
                }
            }
        },
        saveConfig(state, { payload }) {
            return {
                ...state,
                config: payload
            }
        },
        updateConfig(state, { payload }) {
            const newConfig = {
                ...(state.config || {}),
                ...payload
            }

            return {
                ...state,
                config: newConfig
            }
        },
        update(state, { payload }) {
            return {
                ...state,
                ...payload
            }
        }
    }
}

export default UserModel
